import { gql } from '@apollo/client';
import { EXCEPTION_MAPPINGS_FIELD } from '@/graphql/fragments/exception_mappings';
import { WAREHOUSE_EVENT_FIELDS } from '@/graphql/queries/order_wh_events';
import { EXCEPTION_FIELDS } from '@/graphql/queries/db_exceptions';
import { ITEM_FIELDS } from '@/graphql/queries/items';
import { ORDER_FIELDS } from '../../../graphql/queries/orders';
import { LOCATION_FIELDS } from '@/graphql/queries/locations';
import { DRIVER_FIELDS } from '@/graphql/fragments/drivers';
import { ORDER_ROUTE_MAPPINGS } from '@/graphql/queries/order_route_mappings';

export const UPSERT_EVENT = gql`
    ${WAREHOUSE_EVENT_FIELDS}
    ${ORDER_FIELDS}
    ${LOCATION_FIELDS}
    ${EXCEPTION_FIELDS}
    ${EXCEPTION_MAPPINGS_FIELD}
    ${ITEM_FIELDS}
    ${DRIVER_FIELDS}

    mutation UpsertEvent(
        $events: [order_wh_events_insert_input!] = []
        $order: orders_insert_input!
        $update_cols: [order_wh_events_update_column!]! = []
        $order_update_cols: [orders_update_column!]! = []
        $tracking_events: [tracking_events_insert_input!] = []
        $items: [items_insert_input!] = []
        $received_pallet_ids: [uuid!] = []
    ) {
        events: insert_order_wh_events(
            objects: $events
            on_conflict: { constraint: order_wh_events_pkey, update_columns: $update_cols }
        ) {
            returning {
                ...WarehouseEvents
                location {
                    ...LocationFields
                }
                driver {
                    ...DriverFields
                }
                wh_driver {
                    ...DriverFields
                }
                exceptions {
                    ...ExceptionMappingsFields

                    exception {
                        ...ExceptionFields
                    }
                }
            }
        }

        tracking_events: insert_tracking_events(
            objects: $tracking_events
            on_conflict: { constraint: tracking_events_uniq, update_columns: [] }
        ) {
            returning {
                ...TrackingEventFields
            }
        }

        # WMS half of a receive: pallet-less items get their own pallet, already-palletized ones get
        # flipped. Both no-op on empty input, so non-WMS callers pass nothing and behave as before.
        received_items: insert_items(
            objects: $items
            on_conflict: { constraint: items_pkey, update_columns: [pallet_id] }
        ) {
            returning {
                ...ItemFields
            }
        }

        received_pallets: update_pallet_items(
            where: { pallet_id: { _in: $received_pallet_ids } }
            _set: { warehouse_status: "RECEIVED" }
        ) {
            returning {
                pallet_id
                warehouse_status
                warehouse_location
            }
        }

        order: insert_orders_one(
            object: $order
            on_conflict: { constraint: orders_pkey, update_columns: $order_update_cols }
        ) {
            ...OrderFields

            itemsByOrderId {
                ...ItemFields
            }
        }
    }
`;

export const INSERT_EXCEPTION_EVENT = gql`
    ${WAREHOUSE_EVENT_FIELDS}
    ${ORDER_FIELDS}
    ${LOCATION_FIELDS}
    ${EXCEPTION_FIELDS}
    ${EXCEPTION_MAPPINGS_FIELD}
    ${ITEM_FIELDS}
    ${DRIVER_FIELDS}
    ${ORDER_ROUTE_MAPPINGS}

    mutation InsertExceptionEvent(
        $exception_event: insert_exception_event_args!
        $order: orders_insert_input! = {}
        $order_update_cols: [orders_update_column!]! = []
        $attempt_mapping_ids: [uuid!] = []
    ) {
        mappings: insert_exception_event(args: $exception_event) {
            ...ExceptionMappingsFields
        }

        order: insert_orders_one(
            object: $order
            on_conflict: { constraint: orders_pkey, update_columns: $order_update_cols }
        ) {
            ...OrderFields

            itemsByOrderId {
                ...ItemFields
            }

            wh_events(
                order_by: [{ created_at: asc_nulls_first }, { transition: { current: asc } }, { event_id: asc }]
            ) {
                ...WarehouseEvents

                transition {
                    next
                    current
                }

                location {
                    ...LocationFields
                }

                driver {
                    ...DriverFields
                }

                wh_driver {
                    ...DriverFields
                }
            }
        }
        # When a stop attempt is recorded, flip the active mapping to
        # duplicate_override='DUPLICATE'. Scoped by mapping_id so the update
        # hits the exact mapping the caller saw. Default empty array makes
        # this a no-op for happy-path completions.
        route_mapping_duplicate_updates: update_order_route_mappings(
            where: { mapping_id: { _in: $attempt_mapping_ids } }
            _set: { duplicate_override: "DUPLICATE" }
        ) {
            affected_rows
            returning {
                ...OrderRouteMappings
            }
        }
    }
`;

export const INSERT_EXCEPTION = gql`
    ${EXCEPTION_MAPPINGS_FIELD}
    ${EXCEPTION_FIELDS}
    mutation InsertItemExceptions($exception: [exceptions_insert_input!]!) {
        created: insert_exceptions(objects: $exception) {
            returning {
                ...ExceptionFields
                mappings {
                    ...ExceptionMappingsFields
                }
            }
        }
    }
`;

export const UPDATE_EXCEPTION = gql`
    ${EXCEPTION_MAPPINGS_FIELD}
    ${EXCEPTION_FIELDS}
    mutation UpdateItemExceptions(
        $exception_id: uuid!
        $exception: exceptions_set_input
        $mappingsToCreate: [exception_mappings_insert_input!] = []
        $mappingsToDelete: [uuid!] = []
    ) {
        updated: update_exceptions(where: { exception_id: { _eq: $exception_id } }, _set: $exception) {
            returning {
                ...ExceptionFields
            }
        }
        created: insert_exception_mappings(objects: $mappingsToCreate) {
            returning {
                ...ExceptionMappingsFields
            }
        }
        deleted: delete_exception_mappings(where: { mapping_id: { _in: $mappingsToDelete } }) {
            returning {
                ...ExceptionMappingsFields
            }
        }
    }
`;
